import { StrictMode, useCallback, useEffect, useMemo, useState, type ReactNode } from "react";
import { createRoot } from "react-dom/client";

import { createHttpTransport } from "@tdelivr/api-client";

import { arrivalFor, LeavingForTheProvider } from "./auth/arrival.tsx";
import { SessionAuthority, type SignInFailure } from "./auth/authority.ts";
import { isConfigured, readOidcConfiguration } from "./auth/configuration.ts";
import { useAuthentication } from "./auth/use-authentication.ts";
import { developmentSession } from "./development-session.ts";
import { createDriverApi } from "./driver-api.ts";
import { browserQueueStore } from "./offline-queue.ts";
import { pageIdIn, urlForPage } from "./page-location.ts";
import type { Session } from "./session.ts";
import { DocumentUpload } from "./ui/DocumentUpload.tsx";
import { Earnings } from "./ui/Earnings.tsx";
import { deviceIdentity, nextSequence } from "./device.ts";
import { useShiftHeartbeat } from "./shift-heartbeat.ts";
import { GoOnline } from "./ui/GoOnline.tsx";
import { MultiStopJob } from "./ui/MultiStopJob.tsx";
import { OfferInbox } from "./ui/OfferInbox.tsx";
import { OfflineQueue } from "./ui/OfflineQueue.tsx";
import { Proof } from "./ui/Proof.tsx";
import { Scan } from "./ui/Scan.tsx";
import { ScreenOutlook } from "./ui/ScreenOutlook.tsx";
import { SessionEnded } from "./ui/SessionEnded.tsx";
import { SignIn } from "./ui/SignIn.tsx";
import { StateScreen } from "./ui/StateScreen.tsx";
import { ToPickup } from "./ui/ToPickup.tsx";
import { Handoff } from "./ui/Handoff.tsx";
import { OnboardingBoard } from "./ui/OnboardingBoard.tsx";
import { Vehicle } from "./ui/Vehicle.tsx";
import type { LocationPermission } from "./shift-readiness.ts";
import "./styles.css";

/**
 * The driver application, running.
 *
 * Two ways in, and only one of them exists in a production build.
 *
 * **Sign in.** OpenID Connect, authorization code flow with PKCE, against the provider named by
 * `VITE_OIDC_ISSUER`. The shape is not a choice: a browser application cannot hold a client secret,
 * so a public client with PKCE is the only correct option. An unauthenticated arrival does not draw
 * a form — it goes straight to the provider's own page, per ADR 0022 — and `auth/arrival.tsx` holds
 * the decision, including the arrivals that must **not** be redirected because redirecting them is a
 * loop.
 *
 * **Development claims.** `VITE_ACCESS_TOKEN_CLAIMS`, read by `development-session.ts`, which is the
 * path the acceptance check and the tests run on. Compiled out of a production build rather than
 * merely discouraged in it — see that file for the two mechanisms.
 *
 * ## There is no shell, because the design draws none
 *
 * Both consoles have a rail, a top bar, breadcrumbs, a help popover, a notifications bell and an
 * account menu, because `CLAUDE.md` section 3's header kit applies to **every console page**. Neither
 * driver design file draws any of it: no navigation of any kind, no breadcrumbs, no bell, no account
 * menu, and no tenant anywhere. Eighteen screens, each filling the device. So the routing below hands
 * the whole viewport to one screen and adds nothing around it. Recorded as Q-63, which is the
 * question of how a courier is meant to reach the earnings screen at all — the design implies a flow
 * and draws no control for leaving it.
 */

interface AppProps {
  readonly session: Session;
  readonly accessToken?: () => Promise<string | undefined>;
  /** Rendered in place of the screen when the session has ended. */
  readonly expired?: ReactNode;
}

/**
 * The token for a session that has none, which is the development path.
 *
 * Hoisted rather than written inline at the call site, so it is one stable function identity: the
 * stream's effect depends on `accessToken`, and a new arrow on every render would tear the
 * connection down and open a new one four times a second.
 */
const noToken = (): Promise<string | undefined> => Promise.resolve(undefined);

function App({ session, accessToken, expired }: AppProps): ReactNode {
  const [pageId, setPageId] = useState<string>(() => pageIdIn(window.location.href));

  useEffect(() => {
    // Back and forward move between screens rather than out of the application, now that screens are
    // addressable.
    const onPopState = (): void => setPageId(pageIdIn(window.location.href));
    window.addEventListener("popstate", onPopState);
    return (): void => window.removeEventListener("popstate", onPopState);
  }, []);

  /**
   * Move to a screen the application decided on, rather than one a courier asked for.
   *
   * `replaceState`, not `pushState`: a readiness answer that lands on `cannot-go-online` is a
   * correction of the address rather than a step in a journey, and pushing it would put the screen
   * the courier cannot use into their back button.
   */
  const correct = useCallback((next: string) => {
    if (pageIdIn(window.location.href) === next) return;
    window.history.replaceState(null, "", urlForPage(window.location.href, next));
    setPageId(next);
  }, []);

  const transport = useMemo(
    () =>
      createHttpTransport({
        // The page's own origin, not "/". `new URL("v1/…", "/")` throws, so a bare slash would make
        // every request fail — found the hard way on the dispatch console. Same origin is also the
        // right default once the gateway exists: WEB-0743 forbids exposing the services directly, so
        // the application and its API are one origin in every environment that is not a laptop.
        baseUrl: import.meta.env.VITE_PLATFORM_URL ?? window.location.origin,
        tenantId: () => session.tenantId,
        ...(accessToken === undefined ? {} : { authorization: accessToken }),
      }),
    [session.tenantId, accessToken],
  );

  const api = useMemo(() => createDriverApi(transport), [transport]);
  const baseUrl = import.meta.env.VITE_PLATFORM_URL ?? window.location.origin;
  const permission = useLocationPermission();
  const now = useCallback(() => Date.now(), []);

  /*
   * An offer this application was *sent* to, from the address — an override, not the way it finds
   * work.
   *
   * **It used to be the only way.** A courier's own token could not list their own offers: DSP-02
   * published one listing, it required `ride-offer:workload`, and its own 403 description named a
   * courier as the caller it refuses. So the only offer this application could name was one it had
   * been told about, `stream-open` reported `discovery: false` to every real courier, and screen 2
   * said so. R-22.
   *
   * ADR 0103 closed it the way ADR 0040 closed the same gap for stop plans: DSP-02 publishes
   * `GET /v1/drivers/{driverId}/ride-offers`, bounded by the courier's own stored subject, on a
   * scope a courier's own token holds. So the stream discovers the offers made to this courier by
   * itself, and this query parameter is now what `?plan=` is — the way a push notification opens the
   * application on a *particular* offer, rather than the way it learns that any exist.
   *
   * Still read, and still filtered to things shaped like identifiers, because a notification
   * through NTF opening at `?offers=<id>` remains the right behaviour and because a stale entry in
   * an application's own state must not stop a stream opening.
   */
  const offers = useMemo(() => offersIn(window.location.href), [pageId]);

  /*
   * The proof `8 Proof` was told about, from the address, for the same reason.
   *
   * DEL-07 publishes no listing of a courier's proofs and the delivery a courier is on is the
   * absence `4 To pickup` is blocked on, so there is nothing for this application to search. A
   * notification through NTF would open it at `?proof=<id>`. R-33 and Q-77.
   */
  const proofId = useMemo(() => uuidIn(window.location.href, "proof"), [pageId]);

  /*
   * `4 To pickup`'s delivery and `5 Scan`'s label, from the address, for the same reason again.
   *
   * DEL-03's delivery listing requires `delivery:order:workload` "in addition to
   * delivery:order:read" and its own description refuses to narrow itself to the caller's own rows,
   * so a courier's token cannot ask which delivery is theirs. A scan's shipment and parcel come off
   * the label the camera read, and no platform read produces them at all — which is why LOG-06's
   * scan carries the sender's own `scanId` and `sourceSequence` and takes no `Idempotency-Key`.
   *
   * So all three are told, which is where a push notification and a barcode reader plug in. R-33,
   * Q-77 and Q-127.
   */
  const orderId = useMemo(() => uuidIn(window.location.href, "order"), [pageId]);

  /*
   * `Multi-stop job`'s plan and `Document upload`'s processing job, from the address.
   *
   * **The plan is optional and the job is not, and the difference is the point.** ADR 0040 gave
   * this application a listing a courier's own token can ask, so `Multi-stop job` finds its
   * subject by itself and reads `?plan=` only to be sent to a particular one — the case Q-141
   * says nothing published can decide for a courier carrying two. FIL-03 publishes no listing on
   * either surface and derives its `jobId` inside the service from a quarantine event nobody
   * outside it sees, so `?job=` is the only way that screen can name the check it is waiting on.
   * Q-142.
   */
  const planId = useMemo(() => uuidIn(window.location.href, "plan"), [pageId]);
  const jobId = useMemo(() => uuidIn(window.location.href, "job"), [pageId]);
  /*
   * The application this driver is waiting on, named in the address.
   *
   * DRV-02 publishes no listing at any tenancy bound, so there is no operation that answers "which
   * application is mine" and nothing here could look one up. The screen says so when it is absent.
   */
  const applicationId = useMemo(() => uuidIn(window.location.href, "application"), [pageId]);
  /* A specific manifest, when a link names one. Otherwise the screen takes the next sealed one. */
  const manifestId = useMemo(() => uuidIn(window.location.href, "manifest"), [pageId]);
  const shipmentId = useMemo(() => uuidIn(window.location.href, "shipment"), [pageId]);
  const parcelId = useMemo(() => uuidIn(window.location.href, "parcel"), [pageId]);

  /*
   * Where this device thinks it is, as one stable function.
   *
   * Hoisted into a memo for the reason `noToken` is hoisted: `4 To pickup` and `5 Scan` both take it
   * as a prop, and a new arrow on every render would re-run the effect that reads it — which for
   * `5 Scan` would mean spending a sequence number per render against a service that answers
   * `duplicate` to the second one.
   *
   * `enableHighAccuracy` because the thing being measured is a distance to a loading bay rather than
   * a city, and a 15-second timeout because a courier under a canopy must reach a screen that says
   * so rather than a spinner. The browser's own refusal is a rejection, which is the state both
   * screens draw.
   */
  const position = useMemo(
    () => (): Promise<GeolocationPosition> =>
      new Promise<GeolocationPosition>((resolve, reject) => {
        if (!("geolocation" in navigator)) {
          reject(new Error("This device has no geolocation"));
          return;
        }
        navigator.geolocation.getCurrentPosition(resolve, reject, {
          enableHighAccuracy: true,
          timeout: 15_000,
          maximumAge: 30_000,
        });
      }),
    [],
  );

  /*
   * The device's queue, made once per session.
   *
   * Once because the bytes of a capture are held beside the records in the same object, and a store
   * rebuilt on every render would be a store that forgot the photograph it was holding. The records
   * themselves are in `localStorage` and survive a reload; the bytes do not, which is drawn rather
   * than hidden — Q-73.
   */
  const queueStore = useMemo(() => browserQueueStore(), []);

  /*
   * The window the earnings screens are over: the last seven days, ending now.
   *
   * DRV-07 requires both ends and EXP-003 refuses to default either, because defaulting one would be
   * a service deciding what "this week" means on somebody's earnings. The design says `this week`
   * and nothing publishes a tenant's week boundary, so seven days is this application's reading of
   * it and is recorded as Q-66.
   */
  const earningsWindow = useMemo(() => {
    const end = new Date(Date.now());
    const start = new Date(end.getTime() - 7 * 86_400_000);
    return { windowStart: start.toISOString(), windowEnd: end.toISOString() };
  }, [pageId]);

  /*
   * The device's own identity and its next sample number, as one call.
   *
   * Both belong to the device rather than to the heartbeat, and `nextSequence` advances a counter —
   * so it is read at the moment an observation is sent rather than captured when the hook mounts.
   */
  const deviceBeat = useCallback(
    () => ({ deviceId: deviceIdentity().deviceId, sequence: nextSequence() }),
    [],
  );

  /*
   * The shift, kept alive on every screen.
   *
   * Mounted here rather than on the readiness screen, because the screen a courier waits for work
   * on is the offer inbox — and while both heartbeats lived in `GoOnline`, navigating there stopped
   * them. DRV-05's lease then lapsed in sixty seconds and DSP-01 ranked the courier out of every
   * dispatch, with the roster still reading `online · available`. See `shift-heartbeat.ts`.
   *
   * Above the early return below, because a hook may not sit under one.
   */
  useShiftHeartbeat({ api, driverId: session.driverId, device: deviceBeat });

  if (expired !== undefined) return expired;

  const readiness = (asked: "go-online" | "cannot-go-online" | "document-expiring"): ReactNode => (
    <GoOnline
      api={api}
      tenantId={session.tenantId}
      driverId={session.driverId}
      asked={asked}
      onScreenChange={correct}
      scopes={session.scopes}
      locationPermission={permission}
      now={now}
    />
  );

  const inbox = (asked: "online" | "offer" | "offer-expired"): ReactNode => (
    <OfferInbox
      api={api}
      baseUrl={baseUrl}
      tenantId={session.tenantId}
      driverId={session.driverId}
      accessToken={accessToken ?? noToken}
      offers={offers}
      asked={asked}
      onScreenChange={correct}
      now={now}
    />
  );

  /*
   * One branch per built screen, which is also how the completion report counts one.
   *
   * `tooling/scripts/report-completion.mjs` reads `pageId === "…"` out of this file, so a screen is
   * counted exactly when a courier can open it — and a screen routed here without being built to its
   * design would raise that figure while making the product worse. Eight of the thirteen below are
   * the eight EXP-003's three operations serve. `6 Offline queue`'s subject is this handset and
   * `8 Proof`'s is one DEL-07 proof, both reachable because ADR 0025 gave FIL-02 a public surface.
   * The last three arrived with decisions rather than with routes: ADR 0034 made a courier's vehicle
   * an assignment record, which is the whole of `Vehicle`; ADR 0035 gave the platform an address it
   * owns, which is `4 To pickup`'s headline; and `5 Scan` became answerable when LOG-02 published
   * the parcel a scan was of.
   *
   * The last two arrived on 10 October, and each cost one published read. `Document upload` needed
   * FIL-03's processing-job read, which existed only on a prefix ADR 0029 refuses to route and now
   * has a narrowed `/v1` projection under its own scope. `Multi-stop job` needed a way for a
   * courier to **find** a plan whose read already existed and was keyed on an identifier the
   * handset never sees, which is ADR 0040. Every other designed screen falls through to
   * `ScreenOutlook`, which says what it is waiting on.
   */
  return pageId === "go-online" ? (
    readiness("go-online")
  ) : pageId === "cannot-go-online" ? (
    readiness("cannot-go-online")
  ) : pageId === "document-expiring" ? (
    readiness("document-expiring")
  ) : pageId === "online" ? (
    inbox("online")
  ) : pageId === "offer" ? (
    inbox("offer")
  ) : pageId === "offer-expired" ? (
    inbox("offer-expired")
  ) : pageId === "earnings" ? (
    <Earnings
      api={api}
      tenantId={session.tenantId}
      driverId={session.driverId}
      screen="earnings"
      windowStart={earningsWindow.windowStart}
      windowEnd={earningsWindow.windowEnd}
    />
  ) : pageId === "quality-score" ? (
    <Earnings
      api={api}
      tenantId={session.tenantId}
      driverId={session.driverId}
      screen="quality-score"
      windowStart={earningsWindow.windowStart}
      windowEnd={earningsWindow.windowEnd}
    />
  ) : pageId === "offline-queue" ? (
    <OfflineQueue
      api={api}
      tenantId={session.tenantId}
      store={queueStore}
      scopes={session.scopes}
      driverId={session.driverId}
    />
  ) : pageId === "proof" ? (
    <Proof api={api} proofId={proofId} />
  ) : pageId === "to-pickup" ? (
    <ToPickup
      api={api}
      tenantId={session.tenantId}
      driverId={session.driverId}
      orderId={orderId}
      position={position}
      now={now}
    />
  ) : pageId === "scan" ? (
    <Scan
      api={api}
      tenantId={session.tenantId}
      shipmentId={shipmentId}
      parcelId={parcelId}
      position={position}
      now={now}
    />
  ) : pageId === "handoff" ? (
    <Handoff api={api} tenantId={session.tenantId} manifestId={manifestId} />
  ) : pageId === "onboarding-board" ? (
    <OnboardingBoard api={api} tenantId={session.tenantId} applicationId={applicationId} />
  ) : pageId === "vehicle" ? (
    <Vehicle api={api} driverId={session.driverId} now={now} />
  ) : pageId === "multi-stop-job" ? (
    <MultiStopJob
      api={api}
      tenantId={session.tenantId}
      driverId={session.driverId}
      planId={planId}
      position={position}
      now={now}
    />
  ) : pageId === "document-upload" ? (
    <DocumentUpload
      api={api}
      tenantId={session.tenantId}
      store={queueStore}
      jobId={jobId}
      scopes={session.scopes}
    />
  ) : (
    <ScreenOutlook pageId={pageId} />
  );
}

/**
 * The offers named in the address, as uuids only.
 *
 * EXP-003 ignores an entry that is not a uuid rather than refusing the connection, "so one stale
 * identifier in an application's local state cannot stop a stream opening". Filtering here as well
 * means the same thing is true of the request this application sends, and a query of pure nonsense
 * produces a connection with nothing named — which EXP-003 refuses 400, and screen 2 words.
 */
function offersIn(url: string): readonly string[] {
  return (new URL(url).searchParams.get("offers") ?? "")
    .split(",")
    .map((entry) => entry.trim())
    .filter((entry) => UUID.test(entry));
}

const UUID = /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;

/**
 * One identifier from the address, when it is shaped like one.
 *
 * Filtered rather than passed through, for the reason `offersIn` is: a request built from whatever
 * was in the query reaches the service as a 400 with a path segment in it, where a screen that knows
 * it was told nothing usable can say so. `?proof=nonsense` therefore draws the "no proof named"
 * state rather than an error about a route.
 */
function uuidIn(url: string, parameter: string): string | undefined {
  const asked = (new URL(url).searchParams.get(parameter) ?? "").trim();
  return UUID.test(asked) ? asked : undefined;
}

/**
 * Whether the browser will give this page a position.
 *
 * The one fact on the readiness screen that is the application's own rather than the platform's:
 * `Location always on` / `Needed while you're online` is a device permission and no service can
 * answer it. `permissions.query` is watched rather than read once, because a courier granting the
 * permission from the browser's own prompt must turn the row green without a reload.
 *
 * Safari has never implemented `permissions.query` for geolocation, so `unsupported` is a real
 * answer and is a `wait` row rather than a tick — which is the same rule as an unconfirmable gate: a
 * green row over something nothing checked is the one thing this screen must not draw.
 */
function useLocationPermission(): LocationPermission {
  const [permission, setPermission] = useState<LocationPermission>("unknown");

  useEffect(() => {
    if (!("geolocation" in navigator)) {
      setPermission("unsupported");
      return;
    }
    const permissions = navigator.permissions as Permissions | undefined;
    if (permissions === undefined || typeof permissions.query !== "function") {
      setPermission("unsupported");
      return;
    }
    let status: PermissionStatus | undefined;
    const onChange = (): void => {
      if (status !== undefined) setPermission(status.state);
    };
    permissions
      .query({ name: "geolocation" })
      .then((result) => {
        status = result;
        setPermission(result.state);
        result.addEventListener("change", onChange);
      })
      .catch(() => setPermission("unsupported"));
    return (): void => status?.removeEventListener("change", onChange);
  }, []);

  return permission;
}

/**
 * Everything above the screens: leaving to sign in, the code exchange, the expiry and the sign-out.
 *
 * One switch over one state. Exactly one of its branches leaves this page — `signed_out`, and only
 * when `arrivalFor` says the arrival has nothing to report. Every other branch draws something, and
 * that asymmetry is the whole of ADR 0022's caution: a state that is unauthenticated *because
 * something went wrong* must be read, not retried, or the application redirects into the same failure
 * for as long as the browser lets it.
 */
function Authenticated({ authority }: { readonly authority: SessionAuthority }): ReactNode {
  const state = useAuthentication(authority);
  const [dismissedFailure, setDismissedFailure] = useState(false);

  const providerName = authority.providerName;
  const accessToken = useCallback(() => authority.accessToken(), [authority]);
  const signIn = useCallback(() => {
    setDismissedFailure(false);
    void authority.beginSignIn();
  }, [authority]);

  switch (state.kind) {
    case "starting":
      return <StateScreen screen="readiness" state={{ kind: "loading" }} purpose="Starting up." />;

    case "signed_out":
      return arrivalFor(state).kind === "to_the_provider" ? (
        <LeavingForTheProvider authority={authority} />
      ) : (
        <SignIn
          step="start"
          afterSignOut={state.afterSignOut}
          providerName={providerName}
          onContinue={signIn}
          onStartAgain={() => setDismissedFailure(true)}
        />
      );

    case "authenticating":
      return (
        <SignIn
          step="authenticating"
          afterSignOut={false}
          providerName={providerName}
          onContinue={signIn}
          onStartAgain={() => setDismissedFailure(true)}
        />
      );

    case "failed":
      return (
        <SignIn
          step={dismissedFailure ? "start" : "failed"}
          afterSignOut={false}
          providerName={providerName}
          failure={state.failure}
          onContinue={signIn}
          onStartAgain={() => setDismissedFailure(true)}
        />
      );

    case "expired":
      return (
        <App
          session={state.session}
          accessToken={accessToken}
          expired={
            <SessionEnded
              endedAt={state.endedAt}
              lastedMs={state.lastedMs}
              onSignInAgain={() => {
                void authority.beginSignIn();
              }}
            />
          }
        />
      );

    case "signed_in":
      return <App session={state.session} accessToken={accessToken} />;
  }
}

/** A build with no identity provider configured, said in this application's own failure screen. */
function unconfigured(missing: readonly string[]): SignInFailure {
  return {
    code: "app_not_configured",
    headline: "This app has no identity provider configured",
    detail: `It was built without ${missing.join(" and ")}, so there is nowhere to send you to sign in. Whoever builds or serves this app sets those. Retrying won't change this.`,
    retryable: false,
  };
}

const root = document.getElementById("root");
if (root === null) throw new Error("The driver app has no mount point");

/*
 * Rendered synchronously, in every case.
 *
 * Module scripts run before `DOMContentLoaded`, and the acceptance check asks whether an element is
 * visible immediately after that event. A bootstrap that awaited anything before its first render
 * would have the check conclude the screen was absent. The asynchronous part of signing in happens
 * after the first paint, behind the loading state.
 */
const development = import.meta.env.DEV ? developmentSession() : undefined;

if (development !== undefined) {
  createRoot(root).render(
    <StrictMode>
      <App session={development} />
    </StrictMode>,
  );
} else {
  const configuration = readOidcConfiguration(import.meta.env, window.location.origin);
  if (!isConfigured(configuration)) {
    createRoot(root).render(
      <StrictMode>
        <SignIn
          step="failed"
          afterSignOut={false}
          providerName="your organisation's sign-in"
          failure={unconfigured(configuration.missing)}
          onContinue={() => {}}
          onStartAgain={() => {}}
        />
      </StrictMode>,
    );
  } else {
    const authority = new SessionAuthority({
      configuration,
      fetch: globalThis.fetch.bind(globalThis),
      crypto: globalThis.crypto,
      // Per tab. Two tabs signing in at once must not read each other's code verifier, and a
      // verifier left in `localStorage` would outlive the browser session it belongs to.
      storage: window.sessionStorage,
      currentUrl: (): string => window.location.href,
      replaceUrl: (url: string): void => {
        window.history.replaceState(null, "", url);
      },
      navigate: (url: string): void => {
        window.location.assign(url);
      },
      now: (): number => Date.now(),
    });

    createRoot(root).render(
      <StrictMode>
        <Authenticated authority={authority} />
      </StrictMode>,
    );
    // After the first render, so the loading state is on screen while discovery and the code
    // exchange happen.
    void authority.start();
  }
}
